<template>
  <dashboard-widget :is-loading="isLoading" is-panel>
    <template>
      <div class="funnel-chart-animator" ref="funnel-chart-animator">
        <div class="funnel-chart" :key="key">
          <h4>{{ $t('ui.dashboard.conversion_funnel.label') }}</h4>

        <div class="row gutter-sm">
          <div class="col-xs-8">
            <div class="chart-data">
              <div class="chart-funnel">
                <div class="funnel-segment"
                  v-for="(segment, index) in chartSegments"
                  :key="`segment${index}`">
                  <div
                    class="segment-spacer"
                    v-if="index > 0">
                  </div>

                  <svg
                    style="border-radius: 4px"
                    viewBox="0 0 100 100"
                    preserveAspectRatio="none">
                    <defs>
                      <linearGradient
                        :id="`grad${index}`"
                        x1="0%"
                        y1="0%"
                        x2="0%"
                        y2="100%">
                        <stop
                          offset="0%"
                          :style="`stop-color: ${segmentColors[index]}`"/>
                        <stop
                          offset="100%"
                          :style="`stop-color: ${(segmentColors[index])}`"/>
                      </linearGradient>
                    </defs>

                    <polygon
                      points=""
                      :fill="isGradientColors ? `url(#grad${index}` : segmentColors[index]">
                      <animate
                        attributeName="points"
                        :dur="isAnimated ? animationTime : '0.015s'"
                        fill="freeze"
                        :from="'0,0 100,0 100,100 0,100'"
                        keySplines="
                          0.1 0.8 0.2 1;
                          0.1 0.8 0.2 1;
                          0.1 0.8 0.2 1;
                          0.1 0.8 0.2 1;
                          0.1 0.8 0.2 1;
                          0.1 0.8 0.2 1"
                        keyTimes="0;0.22;0.33;0.55;0.66;0.88;1"
                        calcMode="spline"
                        :to="`
                          ${(100 - segment.conversion)},0
                          100,0
                          100,100
                          ${(chartSegments[index + 1] ? (100 - chartSegments[index + 1].conversion) : '100')},100`"
                      />
                    </polygon>
                  </svg>

                  <div class="segment-text">
                    <p class="accent pre">{{ segment.value }}</p>
                    <p class="caption">{{ segment.label }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="col-xs-4">
            <div class="acumulative-panel">
              <div
                :class="`data-${index}`"
                v-for="(step, index) in chartSegments"
                v-if="index > 0"
                :key="`step-${index}`">
                <h4>{{ step.conversion | percentage }}</h4>
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>
    </template>
  </dashboard-widget>
</template>

<script>
import map from 'lodash/map'
import isNull from 'lodash/isNull'
import get from 'lodash/get'
import join from 'lodash/join'
import find from 'lodash/find'
import eq from 'lodash/eq'
import gt from 'lodash/gt'
import size from 'lodash/size'
import flow from 'lodash/flow'
import sum from 'lodash/sum'
import round from 'lodash/round'
import isEmpty from 'lodash/isEmpty'
import slice from 'lodash/fp/slice'
import reject from 'lodash/fp/reject'
import defaultTo from 'lodash/defaultTo'
import cond from 'lodash/cond'
import constant from 'lodash/constant'
import stubTrue from 'lodash/stubTrue'
import each from 'lodash/each'

import Charts from '@/api/Charts.js'
import Api from '@/api/Api.js'
import WidgetMixin from './mixins/WidgetMixin'
import WidgetPeriodMixin from './mixins/WidgetPeriodMixin'
import WidgetQueryMixin from './mixins/WidgetQueryMixin'
import WidgetPercentageMixin from './mixins/WidgetPercentageMixin'

// Returns the ratio of two values. Handles also the case where one of the values is zero
const ratio = (x, y) => cond([
  [({ x, y }) => (x === 0 || y === 0), constant(0)],
  [stubTrue, ({ x, y }) => defaultTo(round((x / y) * 100, 1), 0)]
])({ x, y })

export default {
  mixins: [WidgetMixin, WidgetPeriodMixin, WidgetQueryMixin, WidgetPercentageMixin],

  props: {
    identifier: {
      type: String,
      default: 'conversion-funnel'
    },

    segmentColors: {
      type: Array,
      // One more than the number of chart segments, if gradiation is used
      default: () => ['#e8f2fe', '#bad8fa', '#3c8af5', '#2565c3', '#0e284d']
    },

    isGradientColors: {
      type: Boolean,
      default: true,
    },

    isAnimated: {
      type: Boolean,
      default: true,
    },

    animationTime: {
      type: String,
      default: '0.5s'
    },
  },

  data: () => ({
    chartSegments: [],
    ratios: {
      leadsSendouts: 0,
      sendoutsVisits: 0,
      visitsInteractions: 0
    },

    key: 0,
  }),

  async mounted () {
    this.eventBus.$on('widget-loaded', () => {
      let animator = this.$refs['funnel-chart-animator']
      if (animator) {
        animator.classList.remove('animate')
        setTimeout(() => animator.classList.add('animate'), 10)
      }
    })
  },

  methods: {
    async refresh() {
        this.setIsLoading(true)

        try {
            let conversionFunnel = await this.get(this.from(this.filter.period), this.filter.projectId)
            this.transform(conversionFunnel)
        } catch (error) {
            this.setError(error)
            this.setIsError(true)
        } finally {
            this.setIsLoading(false)
            this.key++
            this.eventBus.$emit('widget-loaded', this.identifier)
        }
    },
    async get (from, project) {
      return new Promise(async (resolve, reject) => {
        let charts = new Charts()

        const tuples = [
          { key: 'from', value: `"${from}"` }
        ]

        if (!!project) {
          tuples.push({ key: 'projectId', value: project })
        }

        const querySchema = `
          leads_count
          sendouts_count
          visits_count
          interactions_count
        `
        const query = this.getQuery(tuples, 'conversionFunnel', querySchema)
        let { conversion_funnel: conversionFunnel } = await charts.postQuery({
          organization_id: this.filter.organizationId,
          operationName: null,
          variables: {},
          query: `query Query {
            ${query}
          }`
        })
        .catch(reject)

        resolve(conversionFunnel)
      })
    },

    // Transform for easier rendering
    transform (conversionFunnel) {
      this.chartSegments = [
        {
          label: this.$t('ui.dashboard.conversion_funnel.leads'),
          value: conversionFunnel.leads_count,
        },
        {
          label: this.$t('ui.dashboard.conversion_funnel.sendouts'),
          value: conversionFunnel.sendouts_count,
        },
        {
          label: this.$t('ui.dashboard.conversion_funnel.visits'),
          value: conversionFunnel.visits_count,
        },
        {
          label: this.$t('ui.dashboard.conversion_funnel.interactions'),
          value: conversionFunnel.interactions_count,
        }
      ]

      let total = 0
      each(this.chartSegments, ((chartSegment, index) => {
        if (eq(index, 0)) {
          total = chartSegment.value

          chartSegment.conversion = chartSegment.value > 0 ? 100 : 0
        } else {
          chartSegment.conversion = ratio(chartSegment.value, total)
        }
      }))
    }
  }
}
</script>

<style lang="sass" scoped>
  .funnel-chart
    font-family: Poppins, sans-serif
    border: 0
    padding: 0
    margin: 0
    width: 100%
    height: 100%
    display: flex
    flex-direction: column


    .chart-title
      text: black
      font-size: 20px
      line-height: 28px
      font-weight: 400
      padding: 0
      margin: 0
      margin-bottom: 20px

    .chart-data
      display: flex
      flex-direction: row
      flex: 1 1 auto
      width: 100%
      height: 100%
      .chart-funnel
        display: flex
        flex-direction: column
        flex: 1 1 auto
        width: 100%
        height: auto

        .funnel-segment
          position: relative
          width: 100%
          height: 100%
          background-color: #f9fafb
          border: 0
          padding: 0
          margin: 0
          margin-bottom: 5px
          border-radius: 4px
          display: flex
          align-items: flex-start
          justify-content: center
          flex-direction: column
          height: 58px
          svg
            position: absolute
            width: 100%
            height: 100%
            stroke-width: 0

        .segment-spacer
          position: absolute
          width: 100%
          height: 5px
          top: -5px
          z-index: 100
          border: 0
          margin: 0
          padding: 0
          background-color: white

        .segment-text
          margin: 0px 0 0 20px
          z-index: 1
          .pre
            font-size: 14px
            line-height: 20px
            font-weight: 500
            position: relative
            margin: 0
            margin-bottom: 0px
          .caption
            margin-bottom: 0px


      .chart-conversion
        display: flex
        flex-direction: column
        flex: 1 1 auto
        width: 30%
        height: 100%

        .conversion-label
          font-size: 12px
          line-height: 16px
          font-weight: 400
          color: rgb(99, 106, 129)
          text-align: center
          margin-left: 20px
          height: 100%
          margin-bottom: 25px

        .conversion-step
          display: flex
          align-items: center
          justify-content: center
          margin-left: 20px
          background-color: #f4f5f8
          height: 100%
          margin-bottom: 25px
          transform: translateY(-25px)

          opacity: 0
          transition: opacity 0.5s
          .animate &
            opacity: 1

          .step-value
            font-size: 20px
            line-height: 28px
            font-weight: 400

  .funnel-chart-animator
    height: 100%

  .acumulative-panel
    display: flex
    flex-direction: column
    align-items: center
    text-align: center
    margin-top: 52px
    h4
      margin: 10px 0px 0px 0px !important
      text-align: center
    .data-1
      background-image: url("../../../../../assets/conversion-1.svg")
      background-repeat: no-repeat
      width: 72px
      height: 44px
      margin-bottom: 18px
    .data-2
      background-image: url("../../../../../assets/conversion-2.svg")
      background-repeat: no-repeat
      width: 72px
      height: 44px
      margin-bottom: 18px
    .data-3
      background-image: url("../../../../../assets/conversion-3.svg")
      background-repeat: no-repeat
      width: 72px
      height: 44px
      margin-bottom: 18px
</style>



// WEBPACK FOOTER //
// ConversionFunnelWidget.vue?524ea692